import React, { useEffect, useRef, useState } from "react";
import { api, formatRupiah, formatApiError } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, FileSpreadsheet, Plus, Check, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import ExportImport from "@/components/ExportImport";
import HistoryButton from "@/components/HistoryButton";

export default function Payroll() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef();

  const load = async () => {
    const [p, u] = await Promise.all([api.get("/payroll"), api.get("/users")]);
    setItems(p.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payroll", {
        ...form,
        basic_salary: Number(form.basic_salary || 0),
        allowances: Number(form.allowances || 0),
        deductions: Number(form.deductions || 0),
        kasbon: Number(form.kasbon || 0),
      });
      toast.success("Payroll dibuat");
      setForm({}); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/payroll/${id}/approve`);
      toast.success("Payroll disetujui — karyawan dinotifikasi");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const downloadSlip = async (id) => {
    try {
      const res = await api.get(`/payroll/${id}/slip.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus payroll?")) return;
    await api.delete(`/payroll/${id}`);
    load();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/payroll/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_payroll.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/import/payroll", fd);
      toast.success(`Import: ${data.inserted} baru, ${data.skipped} dilewati, ${data.errors?.length || 0} error`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  return (
    <div>
      <PageHeader
        title="Payroll"
        subtitle="Rekap gaji karyawan per periode + approval untuk notifikasi ke karyawan"
        testId="payroll-header"
        actions={
          <>
            <ExportImport module="payroll" canImport={false} />
            <Button onClick={downloadTemplate} data-testid="payroll-template-btn" variant="outline" className="rounded-none border-neutral-800 hover:border-[#FFD700] hover:text-[#FFD700] h-11">
              <FileSpreadsheet size={14} className="mr-1" /> Template Bulk
            </Button>
            <Button onClick={() => fileRef.current?.click()} data-testid="payroll-import-btn" variant="outline" className="rounded-none border-neutral-800 hover:border-[#16A34A] hover:text-[#16A34A] h-11">
              <Upload size={14} className="mr-1" /> Import Bulk
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onImport} />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="payroll-add-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Plus size={16} className="mr-1" /> Tambah</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-2xl">
                <DialogHeader><DialogTitle className="font-display font-black text-2xl">Tambah Payroll</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Periode (YYYY-MM)</Label>
                    <Input required value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-02" className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Karyawan</Label>
                    <Select value={form.user_id || ""} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {["basic_salary","allowances","deductions","kasbon"].map((k) => (
                    <div key={k}>
                      <Label className="text-xs font-mono uppercase text-neutral-400">{k.replace("_"," ")}</Label>
                      <Input type="number" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                    </div>
                  ))}
                  <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                    <Button type="submit" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Simpan</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <Section>
        {items.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Periode</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Karyawan</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Pokok</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tunjangan</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Potongan</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Kasbon</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Net</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-neutral-800" data-testid={`payroll-row-${p.id}`}>
                    <TableCell className="font-mono">{p.period}</TableCell>
                    <TableCell className="font-mono">{users.find((u) => u.id === p.user_id)?.name || "-"}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(p.basic_salary)}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(p.allowances)}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(p.deductions)}</TableCell>
                    <TableCell className="font-mono">{formatRupiah(p.kasbon)}</TableCell>
                    <TableCell className="font-mono font-bold text-[#16A34A]">{formatRupiah(p.net_salary)}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono uppercase px-2 py-1 border ${p.status === "approved" ? "border-[#16A34A] text-[#16A34A]" : "border-[#FFD700] text-[#FFD700]"}`}>{p.status || "draft"}</span>
                    </TableCell>
                    <TableCell className="text-right space-x-3">
                      <button onClick={() => downloadSlip(p.id)} data-testid={`payroll-slip-${p.id}`} className="text-[#FFD700] hover:text-white" title="Download slip PDF">
                        <FileText size={14} />
                      </button>
                      <HistoryButton entityType="payroll" entityId={p.id} />
                      {p.status !== "approved" && (
                        <button onClick={() => approve(p.id)} data-testid={`payroll-approve-${p.id}`} className="text-[#16A34A] hover:text-white" title="Setujui & notif karyawan">
                          <Check size={14} />
                        </button>
                      )}
                      <button onClick={() => remove(p.id)} data-testid={`payroll-delete-${p.id}`} className="text-neutral-500 hover:text-[#DC2626]"><Trash2 size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
